import * as React from "react";
import { useState, useEffect } from "react";
import { Check, ChevronDown, Search, FileText } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface Policy {
  id: string;
  title: string;
  description: string | null;
  department: string | null;
  status: string;
  created_at: string;
}

interface PolicySelectorProps {
  value?: string | null;
  onChange: (policyId: string | null) => void;
  placeholder?: string;
  className?: string;
}

export function PolicySelector({
  value,
  onChange,
  placeholder = "Select policy...",
  className,
}: PolicySelectorProps) {
  const [open, setOpen] = useState(false);
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [loading, setLoading] = useState(false);
  const { profile } = useAuth();

  const selectedPolicy = policies.find(policy => policy.id === value);

  useEffect(() => {
    if (open && policies.length === 0) {
      fetchPolicies();
    }
  }, [open]);

  const fetchPolicies = async () => {
    if (!profile?.company_id) return;
    
    setLoading(true);
    try {
      let query = supabase
        .from('policies')
        .select('id, title, description, department, status, created_at')
        .eq('company_id', profile.company_id);

      // Filter based on user role - show policies user has access to
      if (profile.governance_level !== 'admin') {
        query = query.or(
          `status.eq.approved,` +
          `created_by.eq.${profile.id},` +
          `assigned_reviewers.cs.{${profile.id}},` +
          `assigned_approvers.cs.{${profile.id}}`
        );
      }

      const { data, error } = await query
        .order('created_at', { ascending: false })
        .limit(50); // Limit to recent 50 policies

      if (error) throw error;
      setPolicies(data || []);
    } catch (error) {
      console.error('Error fetching policies:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800 border-green-200';
      case 'under_review': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'pending_approval': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'draft': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'rejected': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "justify-between font-normal px-3 py-2 h-auto min-h-[2.5rem] text-left bg-transparent hover:bg-accent",
            className
          )}
        >
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-muted-foreground" />
            <span className={cn("truncate", !selectedPolicy && "text-muted-foreground")}>
              {selectedPolicy ? selectedPolicy.title : placeholder}
            </span>
          </div>
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start" sideOffset={4}>
        <Command>
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
            <CommandInput
              placeholder="Search policies..."
              className="flex-1 px-0 py-3 text-sm border-0 ring-0 outline-none focus:ring-0 bg-transparent"
            />
          </div>
          <CommandList>
            <CommandEmpty>
              {loading ? "Loading policies..." : "No policies found."}
            </CommandEmpty>
            <CommandGroup>
              {/* None/Clear option */}
              <CommandItem
                value=""
                onSelect={() => {
                  onChange(null);
                  setOpen(false);
                }}
              >
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4" /> {/* Spacer for alignment */}
                    <span className="text-muted-foreground">No policy</span>
                  </div>
                  {!value && <Check className="h-4 w-4" />}
                </div>
              </CommandItem>
              
              {policies.map((policy) => (
                <CommandItem
                  key={policy.id}
                  value={`${policy.title} ${policy.department || ''}`}
                  onSelect={() => {
                    onChange(policy.id);
                    setOpen(false);
                  }}
                >
                  <div className="flex items-center justify-between w-full">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium truncate">{policy.title}</span>
                        <Badge 
                          variant="outline" 
                          className={cn("text-xs px-1.5 py-0.5", getStatusColor(policy.status))}
                        >
                          {policy.status.replace('_', ' ')}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        {policy.department && (
                          <span>{policy.department}</span>
                        )}
                        <span>•</span>
                        <span>{formatDate(policy.created_at)}</span>
                      </div>
                      {policy.description && (
                        <p className="text-xs text-muted-foreground mt-1 truncate">
                          {policy.description}
                        </p>
                      )}
                    </div>
                    {value === policy.id && <Check className="h-4 w-4 ml-2 shrink-0" />}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
